
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ref, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../firebase';
import { Product, Review } from '../types';
import { useApp } from '../App';
import { ShoppingCart, Heart, ChevronRight, Plus, Minus, Star, EyeOff, UserCheck, Sparkles, ShieldCheck, Truck, RotateCcw, ImageIcon } from 'lucide-react';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [activeImage, setActiveImage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useApp();

  useEffect(() => {
    if (id) {
      const productRef = ref(db, `products/${id}`);
      onValue(productRef, (snapshot) => {
        setProduct(snapshot.val());
      });

      const reviewsRef = query(ref(db, 'reviews'), orderByChild('productId'), equalTo(id));
      onValue(reviewsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) setReviews((Object.values(data) as Review[]).sort((a: Review, b: Review) => b.createdAt - a.createdAt));
      });
    }
  }, [id]);

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-32 text-center">
        <div className="animate-spin w-12 h-12 border-4 border-amber-700 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-stone-500 font-medium">Ürün yükleniyor...</p>
      </div>
    );
  }

  const images = product.images || [];
  const averageRating = reviews.length > 0 ? reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length : 0;

  return (
    <div className="container mx-auto px-4 md:px-6 pb-20">
      <nav className="flex items-center space-x-2 text-sm text-stone-500 mb-8">
        <Link to="/" className="hover:text-amber-800">Ana Sayfa</Link>
        <ChevronRight size={14} />
        <Link to={`/category/${(product.category || '').toLowerCase()}`} className="hover:text-amber-800 capitalize">{product.category}</Link>
        <ChevronRight size={14} />
        <span className="text-stone-900 font-medium truncate">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-12">
        <div className="space-y-6">
          <div className="relative aspect-square bg-white rounded-[2.5rem] border border-stone-100 overflow-hidden flex items-center justify-center p-12 shadow-sm">
            {showVideo && product.videoUrl ? (
              <video src={product.videoUrl} className="w-full h-full object-cover" controls autoPlay />
            ) : (
              <img 
                src={images[activeImage] || 'https://via.placeholder.com/800'} 
                alt={product.name} 
                className="w-full h-full object-contain mix-blend-multiply" 
              />
            )}
            <div className="absolute bottom-6 right-6 flex flex-col space-y-2">
              <button className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-stone-400 hover:text-red-500 transition-colors">
                <Heart size={20} />
              </button>
            </div>
          </div>

          <div className="flex items-center space-x-4 overflow-x-auto pb-2 scrollbar-hide">
            {images.map((img, i) => (
              <button 
                key={i}
                onClick={() => { setActiveImage(i); setShowVideo(false); }}
                className={`w-20 h-20 flex-shrink-0 rounded-2xl border-2 transition-all p-2 bg-white ${activeImage === i && !showVideo ? 'border-amber-700 shadow-md' : 'border-stone-100 hover:border-stone-200'}`}
              >
                <img src={img} className="w-full h-full object-contain mix-blend-multiply rounded-lg" />
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col">
          <div className="mb-6">
            <span className="text-sm font-bold text-amber-700 uppercase tracking-widest mb-2 block">{product.brand || "Lumina Craft"}</span>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-stone-900 mb-4">{product.name}</h1>
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex items-center text-amber-500">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={20} fill={s <= Math.round(averageRating) ? "currentColor" : "none"} className={s <= Math.round(averageRating) ? "text-amber-500" : "text-stone-200"} />
                ))}
                <span className="text-stone-400 text-sm font-medium ml-2">({reviews.length} Değerlendirme)</span>
              </div>
            </div>
            <div className="flex items-baseline space-x-4 p-6 bg-stone-50 rounded-3xl border border-stone-100">
              <span className="text-4xl font-black text-amber-800">₺{product.price.toLocaleString()}</span>
              {product.oldPrice && <span className="text-xl text-stone-400 line-through">₺{product.oldPrice.toLocaleString()}</span>}
              <span className="ml-auto text-green-600 text-sm font-bold bg-green-50 px-3 py-1 rounded-full border border-green-100">Stokta Var</span>
            </div>
          </div>

          <div className="mb-10 space-y-6">
            <div className="flex items-center space-x-6">
              <span className="text-sm font-bold text-stone-900 uppercase tracking-widest">ADET:</span>
              <div className="flex items-center bg-stone-50 border border-stone-100 rounded-2xl p-1 shadow-sm">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-12 h-12 flex items-center justify-center text-stone-400 hover:text-amber-800"><Minus size={20} /></button>
                <input type="number" value={quantity} readOnly className="w-16 text-center bg-transparent font-black text-stone-900 text-lg outline-none" />
                <button onClick={() => setQuantity(quantity + 1)} className="w-12 h-12 flex items-center justify-center text-stone-400 hover:text-amber-800"><Plus size={20} /></button>
              </div>
            </div>

            <div className="flex gap-4">
              <button 
                onClick={() => addToCart(product, quantity)}
                className="flex-1 bg-amber-800 text-white py-5 rounded-2xl font-bold flex items-center justify-center space-x-3 hover:bg-amber-900 transition-all shadow-xl shadow-amber-900/30"
              >
                <ShoppingCart size={24} />
                <span>Sepete Ekle</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            <div className="flex flex-col items-center p-4 bg-white border border-stone-100 rounded-2xl text-center">
              <ShieldCheck className="text-amber-700 mb-2" size={24} />
              <span className="text-xs font-bold text-stone-900">Güvenli Paket</span>
            </div>
            <div className="flex flex-col items-center p-4 bg-white border border-stone-100 rounded-2xl text-center">
              <Truck className="text-amber-700 mb-2" size={24} />
              <span className="text-xs font-bold text-stone-900">Ücretsiz Kargo</span>
            </div>
            <div className="flex flex-col items-center p-4 bg-white border border-stone-100 rounded-2xl text-center">
              <RotateCcw className="text-amber-700 mb-2" size={24} />
              <span className="text-xs font-bold text-stone-900">14 Gün İade</span>
            </div>
          </div>

          <div className="mb-8">
            <p className="text-stone-600 leading-relaxed mb-8 font-medium italic">{product.description}</p>
          </div>
        </div>
      </div>

      {/* Reviews Section */}
      <div className="mt-20 border-t border-stone-100 pt-20">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-8">
            <div>
              <h3 className="text-3xl font-serif font-bold text-stone-900 mb-2">Müşteri Deneyimleri</h3>
              <p className="text-stone-500 font-medium italic text-sm">Ürünlerimizi deneyimleyen müşterilerimizin gerçek yorumları.</p>
            </div>
            <div className="flex items-center space-x-6 bg-white p-6 rounded-[2rem] border border-stone-100 shadow-sm">
              <div className="text-5xl font-black text-amber-800">{averageRating.toFixed(1)}</div>
              <div>
                <div className="flex text-amber-500 mb-1">
                  {[1,2,3,4,5].map(s => <Star key={s} size={18} fill={s <= Math.round(averageRating) ? "currentColor" : "none"} />)}
                </div>
                <p className="text-[10px] text-stone-400 font-black uppercase tracking-widest">{reviews.length} Toplam Değerlendirme</p>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {reviews.length > 0 ? (
              reviews.map((rev) => (
                <div key={rev.id} className="bg-white p-8 rounded-[2.5rem] border border-stone-50 hover:border-amber-100 transition-all hover:shadow-lg shadow-stone-900/5 group">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-14 h-14 bg-stone-50 rounded-2xl flex items-center justify-center text-amber-800 font-black border border-stone-100 shadow-inner">
                        {rev.isAnonymous ? <EyeOff size={24} className="text-stone-300" /> : rev.userName.charAt(0)}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className="font-bold text-stone-900">{rev.isAnonymous ? 'Anonim Müşteri' : rev.userName}</p>
                          <UserCheck size={14} className="text-green-500" />
                        </div>
                        <div className="flex text-amber-500 mt-1">
                          {[1,2,3,4,5].map(s => <Star key={s} size={14} fill={s <= rev.rating ? "currentColor" : "none"} />)}
                        </div>
                      </div>
                    </div>
                    <span className="text-[10px] font-black text-stone-300 uppercase tracking-widest">{new Date(rev.createdAt).toLocaleDateString('tr-TR')}</span>
                  </div>
                  <div className="pl-18">
                    <p className="text-stone-600 text-sm leading-relaxed italic font-medium">"{rev.comment}"</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-24 text-center bg-stone-50 rounded-[3rem] border-2 border-dashed border-stone-100 flex flex-col items-center">
                <Sparkles size={48} className="text-stone-200 mb-4" />
                <p className="text-stone-400 font-medium italic">Henüz bir değerlendirme yapılmamış.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
